/**
 * Gmail message shapes and the helpers that read them: headers, unread state,
 * body, attachments and inline images. No client or browser APIs here, so the
 * native app reads mail with exactly the web's rules.
 */

export interface GmailHeader {
  name: string;
  value: string;
}

export interface GmailPayloadPart {
  partId?: string;
  mimeType?: string;
  filename?: string;
  headers?: GmailHeader[];
  body?: { size?: number; data?: string; attachmentId?: string };
  parts?: GmailPayloadPart[];
}

export interface GmailMessage {
  id: string;
  threadId: string;
  snippet?: string;
  labelIds?: string[];
  internalDate?: string;
  payload?: GmailPayloadPart;
  historyId?: string;
  sizeEstimate?: number;
}

export interface GmailThread {
  id: string;
  historyId?: string;
  messages: GmailMessage[];
}

export interface GmailListResponse {
  messages?: { id: string; threadId: string }[];
  nextPageToken?: string;
  resultSizeEstimate?: number;
}

export interface GmailLabel {
  id: string;
  name: string;
  type?: 'system' | 'user';
  messagesTotal?: number;
  messagesUnread?: number;
}

export interface GmailProfile {
  emailAddress: string;
  messagesTotal: number;
  threadsTotal: number;
  historyId: string;
}

// ----- header / payload helpers (used by UI to render messages) -----

export function getHeader(msg: GmailMessage, name: string): string {
  const h = msg.payload?.headers?.find((x) => x.name.toLowerCase() === name.toLowerCase());
  return h?.value ?? '';
}

export function isUnread(msg: GmailMessage): boolean {
  return (msg.labelIds ?? []).includes('UNREAD');
}

/** Extract best-available body (prefer HTML, fall back to text). */
export function extractBody(msg: GmailMessage): { html?: string; text?: string } {
  const out: { html?: string; text?: string } = {};
  walkParts(msg.payload, (p) => {
    if (!p.body?.data) return;
    if (p.mimeType === 'text/html' && !out.html) out.html = decodeBase64Url(p.body.data);
    if (p.mimeType === 'text/plain' && !out.text) out.text = decodeBase64Url(p.body.data);
  });
  return out;
}

export interface GmailAttachment {
  attachmentId: string;
  filename: string;
  mimeType: string;
  size: number;
}

export function listAttachments(msg: GmailMessage): GmailAttachment[] {
  const out: GmailAttachment[] = [];
  walkParts(msg.payload, (p) => {
    // Skip parts that are inline images referenced by cid: — those are
    // displayed in the body, not as separate downloadable attachments.
    if (!p.body?.attachmentId || !p.filename) return;
    if (isInlineImage(p)) return;
    out.push({
      attachmentId: p.body.attachmentId,
      filename: p.filename,
      mimeType: p.mimeType ?? 'application/octet-stream',
      size: p.body.size ?? 0,
    });
  });
  return out;
}

export interface InlineImage {
  /** Content-ID without the angle brackets — the value of `cid:` URLs in the body. */
  cid: string;
  mimeType: string;
  /** Set when Gmail returned the bytes as a separate attachment we need to fetch. */
  attachmentId?: string;
  /** Set when Gmail inlined the bytes directly in the message — base64url. */
  inlineData?: string;
}

/**
 * Inline images embedded in multipart/related messages are referenced from
 * the HTML body via `<img src="cid:something@host">`. The browser can't
 * resolve cid: URLs on its own — we have to look at the parts, find each
 * one's Content-ID header, and substitute.
 *
 * Gmail returns small parts inline as base64url in `body.data`, larger ones
 * as a separate `body.attachmentId` we have to fetch. We surface both.
 */
export function listInlineImages(msg: GmailMessage): InlineImage[] {
  const out: InlineImage[] = [];
  walkParts(msg.payload, (p) => {
    if (!isInlineImage(p)) return;
    const cidHeader = p.headers?.find((h) => h.name.toLowerCase() === 'content-id')?.value;
    if (!cidHeader) return;
    if (!p.body?.attachmentId && !p.body?.data) return;
    out.push({
      cid: cidHeader.replace(/^<|>$/g, '').trim(),
      mimeType: p.mimeType ?? 'application/octet-stream',
      attachmentId: p.body?.attachmentId,
      inlineData: p.body?.data,
    });
  });
  return out;
}

function isInlineImage(p: GmailPayloadPart): boolean {
  const mt = (p.mimeType || '').toLowerCase();
  if (!mt.startsWith('image/')) return false;
  const cd = p.headers?.find((h) => h.name.toLowerCase() === 'content-disposition')?.value || '';
  // If explicitly marked as a regular attachment, it's not inline.
  if (/attachment/i.test(cd)) return false;
  // Otherwise: any image part that carries a Content-ID is inline-embedded
  // (Outlook frequently omits Content-Disposition altogether for inline images).
  return !!p.headers?.find((h) => h.name.toLowerCase() === 'content-id');
}

function walkParts(part: GmailPayloadPart | undefined, fn: (p: GmailPayloadPart) => void): void {
  if (!part) return;
  fn(part);
  for (const child of part.parts ?? []) walkParts(child, fn);
}

/**
 * base64url to UTF-8 without atob or TextDecoder, so the native app (Hermes)
 * reads mail bodies the same way. Malformed byte sequences become U+FFFD, as
 * TextDecoder does.
 */
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_INDEX = new Map([...B64].map((c, i) => [c, i]));

function base64UrlToBytes(s: string): number[] {
  const clean = s.replace(/-/g, '+').replace(/_/g, '/').replace(/[^A-Za-z0-9+/]/g, '');
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const char of clean) {
    buffer = (buffer << 6) | (B64_INDEX.get(char) ?? 0);
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }
  return bytes;
}

function utf8Decode(bytes: number[]): string {
  let out = '';
  for (let i = 0; i < bytes.length;) {
    const b = bytes[i];
    const need = b < 0x80 ? 0 : b >= 0xf0 && b <= 0xf4 ? 3 : b >= 0xe0 ? 2 : b >= 0xc2 && b < 0xe0 ? 1 : -1;
    if (need < 0) { out += '\uFFFD'; i += 1; continue; }
    if (need === 0) { out += String.fromCharCode(b); i += 1; continue; }
    let code = b & (0x3f >> need);
    let ok = i + need < bytes.length;
    for (let k = 1; ok && k <= need; k += 1) {
      const c = bytes[i + k];
      if ((c & 0xc0) !== 0x80) ok = false;
      else code = (code << 6) | (c & 0x3f);
    }
    if (!ok) { out += '\uFFFD'; i += 1; continue; }
    out += String.fromCodePoint(code);
    i += need + 1;
  }
  return out;
}

export function decodeBase64Url(s: string): string {
  return utf8Decode(base64UrlToBytes(s));
}

/** Parse "Name <email@x.com>" into pieces. */
export function parseAddress(value: string): { name: string; email: string } {
  const m = value.match(/^\s*"?([^"<]*)"?\s*<([^>]+)>\s*$/);
  if (m) return { name: m[1].trim(), email: m[2].trim() };
  return { name: '', email: value.trim() };
}
